import { useCallback } from 'react';
import type { ProductListItemModel, SessionsInitializationResponse } from 'autogen/swagger/all';
import { useInitializeChatMutation } from 'autogen/swagger/all/hooks';
import type { SimpleEvent } from 'autogen/swagger/comai';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { sendMessage } from '../shared/helpers';
import type { AIChatResponse, AIMessage, ChatMessage, UserMessageContext } from '../shared/types';

type ComAIStore = {
    initialData?: SessionsInitializationResponse;
    setInitialData: (history: SessionsInitializationResponse) => void;
    hasAcceptedTerms: boolean;
    setHasAcceptedTerms: (accepted: boolean) => void;
    showDisclaimer: boolean;
    setShowDisclaimer: (show: boolean) => void;
    inputValue: string;
    setInputValue: (value: string) => void;
    chat: Record<string, ChatMessage>;
    clearChat: () => void;
    productListLoading: boolean;
    setProductListLoading: (loading: boolean) => void;
    isAIResponseLoading: boolean;
    setIsAIResponseLoading: (loading: boolean) => void;
    newestAIMessageId: string | null;
    setNewestAIMessageId: (id: string | null) => void;
    askMoreProduct: ProductListItemModel | null;
    setAskMoreProduct: (product: ProductListItemModel | null) => void;
    addUserMessage: (message: string, context?: UserMessageContext) => void;
    addAIMessage: (chunk: AIChatResponse) => void;
    isInitializing: boolean;
    setIsInitializing: (value: boolean) => void;
};

const useComAIStore = create<ComAIStore>()(
    persist(
        immer<ComAIStore>((set) => ({
            initialData: undefined as SessionsInitializationResponse | undefined,
            hasAcceptedTerms: false,
            setHasAcceptedTerms: (accepted) => set({ hasAcceptedTerms: accepted }),
            setInitialData: (data) => set({ initialData: data }),
            showDisclaimer: true,
            setShowDisclaimer: (show) => set({ showDisclaimer: show }),
            inputValue: '',
            setInputValue: (value) => set({ inputValue: value }),
            chat: {},
            clearChat: () => set({ chat: {} }),
            productListLoading: false,
            setProductListLoading: (loading) => set({ productListLoading: loading }),
            isAIResponseLoading: false,
            setIsAIResponseLoading: (loading) => set({ isAIResponseLoading: loading }),
            newestAIMessageId: null,
            setNewestAIMessageId: (id: string | null) => set({ newestAIMessageId: id }),
            askMoreProduct: null,
            setAskMoreProduct: (product: ProductListItemModel | null) => set({ askMoreProduct: product }),
            isInitializing: false,
            setIsInitializing: (value: boolean) => set({ isInitializing: value }),
            addUserMessage: (message, context) =>
                set((state) => {
                    const id = uuidv4();
                    state.chat[id] = {
                        type: 'user',
                        text: message,
                        context
                    };
                }),
            addAIMessage: (chunk) => {
                const { invocation_id } = chunk;
                useComAIStore.getState().setNewestAIMessageId(invocation_id ?? null);
                set((state) => {
                    state.chat[invocation_id] ??= {
                        type: 'ai',
                        chunks: []
                    };
                    const aiMessage = state.chat[invocation_id] as AIMessage;
                    if (chunk.custom_metadata?.metadata_type === 'product_carousel_chips') {
                        const { chips } = chunk.custom_metadata;
                        aiMessage.chunks.forEach((c) => {
                            if (c.custom_metadata?.metadata_type === 'product_carousel') {
                                c.custom_metadata.products.forEach((product) => {
                                    const matchingChips = chips.find((chip) => chip.id === product.id);
                                    if (matchingChips) {
                                        product.chips = matchingChips.chips;
                                    }
                                });
                            }
                        });
                    }
                    aiMessage.chunks = aiMessage.chunks.filter((c) => c.id !== chunk.id);
                    aiMessage.chunks.push(chunk);
                    if (!chunk.partial) {
                        aiMessage.chunks = aiMessage.chunks.filter((c) => !c.partial);
                    }
                });
            }
        })),
        {
            name: 'comAIStore',
            storage: createJSONStorage(() => sessionStorage),
            partialize: (state) => ({
                hasAcceptedTerms: state.hasAcceptedTerms,
                showDisclaimer: state.showDisclaimer
            })
        }
    )
);

function populateHistory(chatHistory: SimpleEvent[]) {
    chatHistory.forEach((event) => {
        if (event.author === 'user') {
            const text = event.content?.parts?.map((part) => part.text).join('') || '';
            useComAIStore.getState().addUserMessage(text);
        } else {
            useComAIStore.getState().addAIMessage(event);
        }
    });
}

export const useIsComAILoading = () => {
    return useComAIStore((state) => state.isAIResponseLoading || state.productListLoading);
};

export const useInitializeChat = () => {
    const { mutateAsync: initializeChat } = useInitializeChatMutation();
    const setInitialData = useComAIStore((state) => state.setInitialData);
    const clearChat = useComAIStore((state) => state.clearChat);
    const setResponseLoading = useComAIStore((state) => state.setIsAIResponseLoading);

    const init = useCallback(
        async (reset = false) => {
            // Idempotency: if already initialized and not a forced reset, skip.
            const { isAIResponseLoading, isInitializing, setIsInitializing } = useComAIStore.getState();

            // Prevent overlapping inits.
            if (isAIResponseLoading || isInitializing) {
                return;
            }

            setIsInitializing(true);
            setResponseLoading(true);
            if (reset) {
                clearChat();
            }

            const data = await initializeChat({ params: { forceNewChat: reset } });
            setInitialData({ ...data });

            if (data.chatHistory) {
                clearChat();
                populateHistory(data.chatHistory as unknown as SimpleEvent[]);
                setResponseLoading(false);
            } else {
                sendMessage(''); // Get welcome message
            }
            setIsInitializing(false);
        },
        [initializeChat, setInitialData, clearChat, setResponseLoading]
    );

    return init;
};

export const useResetChat = () => {
    useComAIStore.getState().setNewestAIMessageId(null);
    const init = useInitializeChat();

    return () => init(true);
};

export default useComAIStore;
